import { Link, Outlet } from 'react-router-dom';
import { useRouteMatch } from '../../../shared/useRouteMatch';
import {
  indexingDetailPages,
  PageConfigItem,
  pagesConfig,
} from '../../../shared/pages-config';
import {
  getAllBigQueryGroupsCount,
  getAllBigQueryUsersCount,
  getAllCalendarsCount,
  getAllGroupsCount,
  getAllOrgsCount,
  getAllUsersCount,
  UserKeys,
} from '../../../api/users-api';
import { useQuery } from '@tanstack/react-query';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import { ModuleList } from '../../../shared/analytics/module-list';
import { useUserSettings } from '../../../utils/use-user-settings';
import { isAbleToSeeUIElement } from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import SharedTabCard from '../../../shared/shared-tab-card';
import SharedTabContentCard from '../../../shared/shared-tab-content-card';
import Stack from '@mui/material/Stack';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

export default function IndexingPage() {
  const { administrationRoles, usersTier, isDefault, currentTier, features } =
    useUserSettings();

  const { data: allBigQueryUsersCount } = useQuery({
    queryKey: [UserKeys.AllUsersCount],
    queryFn: () => getAllBigQueryUsersCount(),
    enabled:
      currentTier === 'enterprise' &&
      isFeatureEnabled('indexUsersReact', features),
  });

  const { data: allBigQueryGroupsCount } = useQuery({
    queryKey: [UserKeys.AllGroupsCount],
    queryFn: () => getAllBigQueryGroupsCount(),
    enabled:
      currentTier === 'enterprise' &&
      isFeatureEnabled('indexGroupsReact', features),
  });

  const { data: allUsersCount } = useQuery({
    queryKey: [UserKeys.AllUsersCount],
    queryFn: () => getAllUsersCount(),
  });

  const { data: allGroupsCount } = useQuery({
    queryKey: [UserKeys.AllGroupsCount],
    queryFn: () => getAllGroupsCount(),
  });

  const { data: allCalendarsCount } = useQuery({
    queryKey: [UserKeys.AllCalendarsCount],
    queryFn: () => getAllCalendarsCount(),
  });

  const { data: allOrgsCount } = useQuery({
    queryKey: [UserKeys.AllOrgsCount],
    queryFn: () => getAllOrgsCount(),
  });

  const usersCount =
    allUsersCount?.toLocaleString('en', { useGrouping: true }) || '0';
  const bigQueryUsersCount =
    allBigQueryUsersCount?.toLocaleString('en', { useGrouping: true }) || '0';
  const groupsCount =
    allGroupsCount?.toLocaleString('en', { useGrouping: true }) || '0';
  const bigQueryGroupsCount =
    allBigQueryGroupsCount?.toLocaleString('en', { useGrouping: true }) || '0';
  const calendarsCount =
    allCalendarsCount?.toLocaleString('en', { useGrouping: true }) || '0';
  const orgsCount =
    allOrgsCount?.toLocaleString('en', { useGrouping: true }) || '0';

  const routeMatch = useRouteMatch([
    `${pagesConfig.indexing.link}/${pagesConfig.indexingUsers.link}`,
    `${pagesConfig.indexing.link}/${pagesConfig.indexingGroups.link}`,
    `${pagesConfig.indexing.link}/${pagesConfig.indexingCalendars.link}`,
    `${pagesConfig.indexing.link}/${pagesConfig.indexingOrgs.link}`,
  ]);
  const currentTab =
    routeMatch?.pattern?.path ?? `${pagesConfig.indexingUsers.link}`;

  const indexingBreadcrumbMap: Record<string, string> = {
    [pagesConfig.indexingUsers.link]: pagesConfig.indexingUsers.name,
    [pagesConfig.indexingGroups.link]: pagesConfig.indexingGroups.name,
    [pagesConfig.indexingCalendars.link]: pagesConfig.indexingCalendars.name,
    [pagesConfig.indexingOrgs.link]: pagesConfig.indexingOrgs.name,
  };

  const tabValue = currentTab.split('/').pop();

  const activeIndexingBreadcrumb = tabValue
    ? indexingBreadcrumbMap[tabValue]
    : undefined;

  function getBadgeContent(page: PageConfigItem) {
    if (
      page.name === 'Users' &&
      currentTier === 'enterprise' &&
      isFeatureEnabled('indexUsersReact', features)
    )
      return bigQueryUsersCount;
    if (page.name === 'Users') return usersCount;
    if (
      page.name === 'Groups' &&
      currentTier === 'enterprise' &&
      isFeatureEnabled('indexGroupsReact', features)
    )
      return bigQueryGroupsCount;
    if (page.name === 'Groups') return groupsCount;
    if (page.name === 'Calendars') return calendarsCount;
    if (page.name === 'Org Units') return orgsCount;
    return '0';
  }

  return (
    <SimpleBreadcrumbsPage
      breadcrumbs={[
        { label: ModuleList.modules.administration.name },
        { label: pagesConfig.indexing.name },
        ...(activeIndexingBreadcrumb
          ? [{ label: activeIndexingBreadcrumb }]
          : []),
      ]}
      bookmarkKey={pagesConfig.indexing.value}
      isSubTabs
    >
      <Box>
        <Stack>
          <SharedTabCard>
            <Tabs
              value={currentTab.split('/').pop()}
              aria-label="Users"
              sx={{
                mx: 1,
                '& .MuiTabs-flexContainer': {
                  marginTop: '6px',
                },
                '.MuiTabs-scrollButtons.Mui-disabled': {
                  opacity: 0.3,
                },
              }}
            >
              {indexingDetailPages.map((page) => {
                const doesntHavePermission =
                  !isAbleToSeeUIElement(
                    administrationRoles,
                    page.requiredPermission
                  ) && page.requiredPermission !== '*';
                const doesntHaveTier = usersTier.rank < page.minimumTier;
                const shouldShowButDisabled = isDefault && doesntHaveTier;
                if (shouldShowButDisabled) {
                  return (
                    <Tab
                      key={page.name}
                      value={page.link}
                      component={Link}
                      to={page.link}
                      label={
                        <Tooltip
                          title={Messages.Errors.Tier.Viewing(
                            page.minimumTierDisplayValue
                          )}
                          onClick={(e) => e.preventDefault()}
                          arrow
                        >
                          <span style={{ pointerEvents: 'auto' }}>
                            {page.name}
                          </span>
                        </Tooltip>
                      }
                      icon={<page.icon />}
                      iconPosition="start"
                      sx={{
                        minHeight: '40px',
                        height: '40px',
                        width: 'fit-content',
                      }}
                      disabled
                    />
                  );
                }
                return (
                  !(doesntHavePermission || doesntHaveTier) && (
                    <Tab
                      key={page.name}
                      label={page.name}
                      value={page.link}
                      component={Link}
                      to={page.link}
                      iconPosition="start"
                      sx={{ minHeight: '44px', height: '44px' }}
                      icon={
                        <Badge
                          max={Infinity}
                          badgeContent={getBadgeContent(page)}
                          color="primary"
                        >
                          <page.icon />
                        </Badge>
                      }
                    />
                  )
                );
              })}
            </Tabs>
          </SharedTabCard>
          <SharedTabContentCard>
            <Outlet />
          </SharedTabContentCard>
        </Stack>
      </Box>
    </SimpleBreadcrumbsPage>
  );
}
